"use client";

import { useState, useEffect } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { Timer, Menu, X, Globe } from "lucide-react";
import { Container } from "@/components/ui/Container";
import AzurysPicto from "@/assets/azurys-picto.svg";
import { useLanguage } from "@/contexts/LanguageContext";

const navItemsEn = [
  { id: "services", label: "Services", href: "#services" },
  { id: "process", label: "Process", href: "#process" },
  { id: "offres", label: "Offers", href: "#offres" },
  { id: "faq", label: "FAQ", href: "#faq" },
];

const navItemsFr = [
  { id: "services", label: "Services", href: "#services" },
  { id: "process", label: "Processus", href: "#process" },
  { id: "offres", label: "Offres", href: "#offres" },
  { id: "faq", label: "FAQ", href: "#faq" },
];

export function HeaderNav() {
  const [activeItem, setActiveItem] = useState<string | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const { lang, toggleLang } = useLanguage();
  const shouldReduceMotion = useReducedMotion();
  
  const navItems = lang === "en" ? navItemsEn : navItemsFr;
  const ctaText = lang === "en" ? "Start the timer" : "Lancer le chrono";

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  const handleItemClick = (id: string) => {
    setActiveItem(id);
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 pt-4">
      <Container className="flex justify-center">
        {/* Desktop Navigation Pill */}
        <nav className="hidden md:flex items-center justify-between gap-2 rounded-full border border-white/[0.08] backdrop-blur-xl px-7" style={{
          width: "clamp(320px, 50vw, 760px)",
          height: "75px",
          background: "rgba(11, 16, 32, 0.7)"
        }}>
          {/* Logo */}
          <motion.a href="#hero" className="px-2" whileHover={{ scale: 1.02 }}>
            <img src={AzurysPicto} alt="Azurys" className="h-8" />
          </motion.a>

          {/* Nav Items */}
          <div className="flex items-center gap-1 relative">
            {navItems.map(item => (
              <motion.a
                key={item.id}
                href={item.href}
                onClick={() => handleItemClick(item.id)}
                className={`relative px-4 py-2 text-sm font-medium transition-colors duration-200 z-10 ${activeItem === item.id ? "text-foreground" : "text-muted-foreground hover:text-foreground"}`}
                whileHover={{ opacity: 1 }}
                whileTap={{ scale: shouldReduceMotion ? 1 : 0.98 }}
              >
                {/* Active highlight bubble */}
                {activeItem === item.id && (
                  <motion.div
                    layoutId={shouldReduceMotion ? undefined : "nav-highlight"}
                    className="absolute inset-0 rounded-full border border-white/[0.1]"
                    style={{ background: "rgba(180, 255, 180, 0.06)" }}
                    transition={{ type: "spring", stiffness: 400, damping: 30 }}
                  />
                )}
                <span className="relative z-10">{item.label}</span>
                {/* Hover underline */}
                <span className="absolute bottom-1 left-1/2 -translate-x-1/2 w-0 h-px bg-primary group-hover:w-3/4 transition-all duration-300" />
              </motion.a>
            ))}
          </div>

          {/* CTA Button */}
          <motion.a
            href="#contact"
            className="flex items-center gap-2 px-5 py-2 rounded-full border border-white/[0.12] text-foreground text-sm font-medium transition-all duration-200 hover:bg-white/[0.05] hover:border-white/[0.2]"
            whileHover={{ scale: shouldReduceMotion ? 1 : 1.02 }}
            whileTap={{ scale: shouldReduceMotion ? 1 : 0.98 }}
          >
            <Timer size={16} className="text-foreground" />
            <span>{ctaText}</span>
          </motion.a>

          {/* Language Toggle */}
          <motion.button
            onClick={toggleLang}
            className="flex items-center gap-2 px-3 py-2 rounded-full border border-white/[0.12] text-foreground text-sm font-medium transition-all duration-200 hover:bg-white/[0.05] hover:border-white/[0.2]"
            whileHover={{ scale: shouldReduceMotion ? 1 : 1.02 }}
            whileTap={{ scale: shouldReduceMotion ? 1 : 0.98 }}
            aria-label="Toggle language"
          >
            <Globe size={16} />
            <span>{lang === "en" ? "EN" : "FR"}</span>
          </motion.button>
        </nav>

        {/* Mobile Header */}
        <div className="md:hidden flex items-center justify-between w-full rounded-full border border-white/[0.08] backdrop-blur-xl px-7" style={{
          height: "71px",
          background: "rgba(11, 16, 32, 0.7)"
        }}>
          <a href="#hero">
            <img src={AzurysPicto} alt="Azurys" className="h-7" />
          </a>
          <button onClick={() => setIsOpen(!isOpen)} className="p-2 text-foreground" aria-label={isOpen ? "Close menu" : "Open menu"}>
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </Container>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: shouldReduceMotion ? 0 : 0.2 }}
            className="md:hidden fixed inset-x-0 top-20 bottom-0 bg-background/95 backdrop-blur-xl z-40"
          >
            <Container className="py-8">
              <ul className="flex flex-col gap-4">
                {navItems.map((item, index) => (
                  <motion.li
                    key={item.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: shouldReduceMotion ? 0 : index * 0.05 }}
                  >
                    <a
                      href={item.href}
                      onClick={() => {
                        handleItemClick(item.id);
                        setIsOpen(false);
                      }}
                      className={`block text-xl font-medium py-2 transition-colors ${activeItem === item.id ? "text-foreground" : "text-muted-foreground"}`}
                    >
                      {item.label}
                    </a>
                  </motion.li>
                ))}
                <motion.li
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: shouldReduceMotion ? 0 : 0.2 }}
                  className="pt-4 flex flex-wrap gap-3"
                >
                  <a
                    href="#contact"
                    onClick={() => setIsOpen(false)}
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-white/[0.12] text-foreground font-medium"
                  >
                    <Timer size={18} />
                    {ctaText}
                  </a>
                  <button
                    onClick={toggleLang}
                    className="inline-flex items-center gap-2 px-4 py-3 rounded-full border border-white/[0.12] text-foreground font-medium"
                  >
                    <Globe size={18} />
                    {lang === "en" ? "EN" : "FR"}
                  </button>
                </motion.li>
              </ul>
            </Container>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
